import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ
} from '../../services/landingPageService';

const { FiPlus, FiEdit2, FiTrash2, FiHelpCircle, FiX, FiChevronDown, FiChevronUp } = FiIcons;

export default function FAQsTab({ eventId }) {
  const [faqs, setFaqs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    loadFaqs();
  }, [eventId]);

  async function loadFaqs() {
    try {
      setLoading(true);
      const { data, error } = await getEventFAQs(eventId);
      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('[FAQsTab] Error loading FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(faq) {
    setEditingFaq(faq);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingFaq(null);
    setShowModal(true);
  }

  async function handleDelete(faqId) {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const { error } = await deleteFAQ(faqId);
      if (error) throw error;

      await loadFaqs();
      toast.success('FAQ deleted');
    } catch (error) {
      console.error('[FAQsTab] Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  }

  async function handleSave(faqData) {
    try {
      if (editingFaq) {
        const { error } = await updateFAQ(editingFaq.id, faqData);
        if (error) throw error;
        toast.success('FAQ updated');
      } else {
        const maxOrder = faqs.reduce((max, f) => Math.max(max, f.display_order || 0), 0);
        const { error } = await createFAQ({
          ...faqData,
          event_id: eventId,
          display_order: maxOrder + 1
        });
        if (error) throw error;
        toast.success('FAQ created');
      }

      setShowModal(false);
      setEditingFaq(null);
      await loadFaqs();
    } catch (error) {
      console.error('[FAQsTab] Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    }
  }

  function toggleExpand(faqId) {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Add common questions and answers for attendees
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add FAQ
        </button>
      </div>

      {/* FAQs List */}
      <div className="space-y-3">
        {faqs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiHelpCircle} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No FAQs added yet</p>
            <button
              onClick={handleAdd}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first FAQ
            </button>
          </div>
        ) : (
          faqs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(faq.id)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <SafeIcon
                    icon={expandedFaq === faq.id ? FiChevronUp : FiChevronDown}
                    className="w-5 h-5 text-gray-400"
                  />
                  <h4 className="font-medium text-gray-900">{faq.question}</h4>
                </div>

                <div className="flex items-center space-x-1 ml-4" onClick={(e) => e.stopPropagation()}>
                  {faq.category && (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded mr-2">
                      {faq.category}
                    </span>
                  )}
                  <button
                    onClick={() => handleEdit(faq)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {expandedFaq === faq.id && (
                <div className="px-4 pb-4 pl-12">
                  <p className="text-gray-600 whitespace-pre-wrap">{faq.answer}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <FAQModal
          faq={editingFaq}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingFaq(null);
          }}
        />
      )}
    </div>
  );
}

function FAQModal({ faq, onSave, onClose }) {
  const [formData, setFormData] = useState({
    question: faq?.question || '',
    answer: faq?.answer || '',
    category: faq?.category || ''
  });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.question.trim()) {
      toast.error('Question is required');
      return;
    }
    if (!formData.answer.trim()) {
      toast.error('Answer is required');
      return;
    }

    setSaving(true);
    await onSave(formData);
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {faq ? 'Edit FAQ' : 'Add FAQ'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question *
            </label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., What is the refund policy?"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Answer *
            </label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Provide a clear and helpful answer..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category (optional)
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a category...</option>
              <option value="general">General</option>
              <option value="registration">Registration</option>
              <option value="payment">Payment</option>
              <option value="content">Content</option>
              <option value="technical">Technical</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : faq ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
